import type { ServerActionResult } from '@jaaq/observability';
import { z } from 'zod';
import { type OnboardingState, VARIANTS } from '#lib/steps/state';

// The persisted slice of the wizard – every answer the user gave, minus the
// transient `currentStep`. The schema is the validation boundary for the
// `saveOnboardingResults` server action; the shape is intentionally loose (all
// optional) while the wizard is still in flux, mirroring the nullable jsonb column.
export const OnboardingResultsSchema = z.object({
  outcomes: z.array(z.enum(['wellbeing-space', 'embedded-tools', 'not-sure'])).optional(),
  digitalSetups: z.array(z.enum(['no-platform', 'has-website', 'has-dev-team'])).optional(),
  priority: z.enum(['speed', 'integration', 'grow']).optional(),
  recommendedVariant: z.enum(VARIANTS).optional(),
  audience: z
    .object({
      category: z.enum(['education', 'healthcare', 'other-companies']),
      audienceType: z.enum(['staff', 'students', 'both', 'patients']),
    })
    .optional(),
  hostedTier: z.enum(['standard', 'custom']).optional(),
});

export type OnboardingResults = z.infer<typeof OnboardingResultsSchema>;

// Drop only the transient `currentStep`; the answer fields are never written
// as `undefined` (the wizard's `patch` merges defined values), so the rest is
// already JSON-safe, and the schema list stays the single source of truth.
export const buildOnboardingResults = ({
  currentStep: _currentStep,
  ...answers
}: OnboardingState): OnboardingResults => answers;

// The save boundary the wizard depends on, provided by the host. The persisting
// action stays in `hub` because it resolves the signed-in user's company from
// the better-auth session server-side (the package can't reach `hub`'s auth, and
// for security the company must not come from the client). The host injects it
// through `OnboardingRouter`, so the package owns the contract, not the wiring.
export type SaveOnboardingResults = (
  results: OnboardingResults,
) => Promise<ServerActionResult<null>>;
